import { useState } from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { 
  CheckCircle, 
  XCircle, 
  RefreshCw, 
  Activity,
  Database,
  Zap,
  Loader2
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API_URL } from '../config/api';

const SystemHealthTab = () => {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);

  const runHealthCheck = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("jwtToken");
      
      // Check backend
      const backendCheck = await axios.get(`${API_URL}/health`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(() => ({ status: 'healthy', name: 'Backend API' }))
        .catch(() => ({ status: 'error', name: 'Backend API' }));

      // Check database
      const dbCheck = await axios.get(`${API_URL}/health/check`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => ({ 
        status: res.data.database ? 'healthy' : 'error', 
        name: 'MongoDB Database' 
      })).catch(() => ({ status: 'error', name: 'MongoDB Database' }));

      setHealthData({
        backend: backendCheck,
        database: dbCheck,
        timestamp: new Date().toISOString()
      });
      
      setLastChecked(new Date());
      toast.success("Health check completed!");
    } catch (error) {
      console.error("Health check error:", error);
      toast.error("Health check failed");
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    if (status === 'healthy') return <CheckCircle className="h-5 w-5 text-green-600" />;
    return <XCircle className="h-5 w-5 text-red-600" />;
  };

  const getStatusBadge = (status) => {
    if (status === 'healthy') return <Badge className="bg-green-100 text-green-800">Healthy</Badge>;
    return <Badge className="bg-red-100 text-red-800">Error</Badge>;
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">System Health</h2>
          <p className="text-gray-600">Monitor platform status</p>
        </div>
        <Button
          onClick={runHealthCheck}
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Checking...
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4 mr-2" />
              Run Health Check
            </>
          )}
        </Button>
      </div>

      {/* Last Checked */}
      {lastChecked && (
        <p className="text-sm text-gray-500">
          Last checked: {lastChecked.toLocaleTimeString()}
        </p>
      )}

      {/* Health Status */}
      {healthData ? (
        <div className="space-y-4">
          {/* Backend */}
          <div className="p-6 bg-white border border-gray-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {getStatusIcon(healthData.backend.status)}
                <div>
                  <h3 className="font-semibold text-gray-900">Backend API</h3>
                  <p className="text-sm text-gray-600">FastAPI server status</p>
                </div>
              </div>
              {getStatusBadge(healthData.backend.status)}
            </div>
          </div>

          {/* Database */}
          <div className="p-6 bg-white border border-gray-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {getStatusIcon(healthData.database.status)}
                <div>
                  <h3 className="font-semibold text-gray-900">MongoDB Database</h3>
                  <p className="text-sm text-gray-600">Database connection status</p>
                </div>
              </div>
              {getStatusBadge(healthData.database.status)}
            </div>
          </div>

          {/* Overall Status */}
          <div className={`p-6 rounded-lg border ${
            healthData.backend.status === 'healthy' && healthData.database.status === 'healthy'
              ? 'bg-green-50 border-green-200'
              : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-center gap-3">
              <Activity className={`h-6 w-6 ${
                healthData.backend.status === 'healthy' && healthData.database.status === 'healthy'
                  ? 'text-green-600'
                  : 'text-red-600'
              }`} />
              <div>
                <h3 className="font-semibold text-gray-900">
                  {healthData.backend.status === 'healthy' && healthData.database.status === 'healthy'
                    ? 'All Systems Operational'
                    : 'System Issues Detected'}
                </h3>
                <p className="text-sm text-gray-600">
                  {healthData.backend.status === 'healthy' && healthData.database.status === 'healthy'
                    ? 'Platform is running smoothly'
                    : 'Some services need attention'}
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-12 text-center bg-gray-50 border border-gray-200 rounded-lg">
          <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Click "Run Health Check" to check system status</p>
        </div>
      )}
    </div>
  );
};

export default SystemHealthTab;
